import React, { type FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import { spacing, useBooleanState } from '@retailer-platform/shared-components'
import { css } from '@emotion/react'
import { PartnerPickDevicesListFilters } from 'domains/partner-pick-devices/components/list/components/filters/PartnerPickDevicesListFilters'
import { usePartnerPickDevicesListQueryParams } from 'domains/partner-pick-devices/components/list/utils/partnerPickDevicesListQueryParams.hooks'
import { usePartnerPickDevicesSearch } from 'domains/partner-pick-devices/api/partnerPickDevicesSearch.hooks'
import { usePartnerPickDevicesListColumns } from 'domains/partner-pick-devices/components/list/utils/partnerPickDevicesListColumns.hooks'
import { usePartnerPickDevicesNotification } from 'domains/partner-pick-devices/components/list/utils/usePartnerPickDevicesNotification.hooks'
import { PartnerPickDevicesList } from 'domains/partner-pick-devices/components/list/components/list/PartnerPickDevicesList'
import {
  type PartnerPickDeviceDatum,
  PartnerPickDeviceStatusFilterEnum,
} from 'domains/partner-pick-devices/types/partnerPickDevices.types'
import { PartnerPickDevicesModal } from 'domains/partner-pick-devices/components/modal/PartnerPickDevicesModal'
import { useWarehouseContext } from 'utils/contexts/warehouse/WarehouseContext.hooks'

interface PartnerPickDevicesListContainerProps {}

const pageWrapper = css({
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
})

const filterWrapper = css({
  marginTop: `-${spacing.X16}`,
  padding: `${spacing.X16} ${spacing.X24}`,
})

const gridWrapper = css({
  flex: 1,
  padding: `0 ${spacing.X20}`,
  overflow: 'hidden',
})

export const PartnerPickDevicesListContainer: FunctionComponent<
  PartnerPickDevicesListContainerProps
> = () => {
  // manage add/edit modal state
  const [isModalOpen, showModal, hideModal] = useBooleanState(false)
  const [deviceIdToEdit, setDeviceIdToEdit] = useState<PartnerPickDeviceDatum['id']>()
  const editDevice = useCallback(
    (deviceId: PartnerPickDeviceDatum['id']) => {
      setDeviceIdToEdit(deviceId)
      showModal()
    },
    [setDeviceIdToEdit, showModal]
  )
  const closeModal = useCallback(() => {
    hideModal()
    setDeviceIdToEdit(undefined)
  }, [setDeviceIdToEdit, hideModal])

  // get warehouse locations from context
  const warehouseContext = useWarehouseContext()
  const warehouseLocations = useMemo(() => {
    if (!warehouseContext.loaded) {
      return []
    }
    return [
      ...warehouseContext.warehouse.warehouseLocations,
      ...warehouseContext.warehouse.inactiveWarehouseLocations,
    ]
  }, [warehouseContext])

  // Setup query params in page url
  const {
    queryParams: filterParams,
    setWarehouseLocationId,
    setStatus,
    setPage,
  } = usePartnerPickDevicesListQueryParams({
    warehouseLocations,
  })

  /** Query params formatted into graphql query variables */
  const queryVariables = useMemo(() => {
    let activeFilter: boolean

    if (filterParams.status === PartnerPickDeviceStatusFilterEnum.Active) {
      activeFilter = true
    } else if (filterParams.status === PartnerPickDeviceStatusFilterEnum.Inactive) {
      activeFilter = false
    }

    return {
      warehouseLocationId: filterParams.warehouseLocationId,
      active: activeFilter,
      page: filterParams.page,
    }
  }, [filterParams.warehouseLocationId, filterParams.status, filterParams.page])

  // setup graphql query hook for loading list of devices
  const { fetchPartnerPickDevices, fetchMore, apiData, apiError, apiLoading } =
    usePartnerPickDevicesSearch()

  const refetch = useCallback(() => {
    fetchPartnerPickDevices(queryVariables)
  }, [fetchPartnerPickDevices, queryVariables])

  useEffect(() => {
    // trigger fetch query whenever queryVariables change but only if page = 1
    // this will be the case whenever either the warehouseLocation or status filters are changed
    // when page > 1, loadMore will be triggered instead
    if (queryVariables.page === 1) {
      fetchPartnerPickDevices(queryVariables)
    }
  }, [fetchPartnerPickDevices, queryVariables, queryVariables.page])

  // setup pagination
  const canLoadMore = useMemo(
    () => apiData?.partnerPickDeviceSearch.meta.totalPages > queryVariables.page,
    [apiData, queryVariables.page]
  )
  const loadMore = useCallback(() => {
    fetchMore({
      ...queryVariables,
      page: queryVariables.page + 1,
    })
    setPage(queryVariables.page + 1)
  }, [fetchMore, queryVariables, setPage])

  const showCardStatus = useMemo(
    () =>
      (apiData?.partnerPickDeviceSearch?.devices ?? []).some(({ marqetaCardsMetadata }) =>
        Object.keys(marqetaCardsMetadata).every(field => marqetaCardsMetadata[field] !== null)
      ),
    [apiData]
  )

  // setup column definitions for the list component
  const columns = usePartnerPickDevicesListColumns({
    onEdit: editDevice,
    onStatusUpdate: refetch,
    showCardStatus,
  })

  // setup behaviour post- device save
  const { notify } = usePartnerPickDevicesNotification()
  const onDeviceSave = useCallback(
    (savedDevice: PartnerPickDeviceDatum) => {
      notify(savedDevice, !deviceIdToEdit)
      refetch()
      closeModal()
    },
    [notify, deviceIdToEdit, closeModal, refetch]
  )

  return (
    <div css={pageWrapper}>
      <div css={filterWrapper}>
        <PartnerPickDevicesListFilters
          filterParams={filterParams}
          warehouseLocations={warehouseLocations}
          setWarehouseLocationId={setWarehouseLocationId}
          setStatus={setStatus}
          onAdd={showModal}
        />
      </div>

      <div css={gridWrapper}>
        <PartnerPickDevicesList
          columns={columns}
          apiData={apiData}
          apiLoading={apiLoading}
          apiError={apiError}
          canLoadMore={canLoadMore}
          loadMore={loadMore}
          refetch={refetch}
        />
      </div>

      {isModalOpen && (
        <PartnerPickDevicesModal
          deviceId={deviceIdToEdit}
          warehouseLocationId={filterParams.warehouseLocationId}
          onSave={onDeviceSave}
          onClose={closeModal}
          warehouseLocations={warehouseLocations}
        />
      )}
    </div>
  )
}
